import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { getErrorMessage } from '../../lib/utils';
import { 
  ArrowLeft, Package, CheckCircle, Clock, AlertTriangle,
  ChevronRight, FileText, RefreshCw
} from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '../../components/ui/drawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResgatesOperador() {
  const navigate = useNavigate();
  const { username: routeUsername } = useParams(); // Para rota de simulação
  const isSimulation = !!routeUsername;
  
  const [user, setUser] = useState(null);
  const [resgates, setResgates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResgate, setSelectedResgate] = useState(null);
  const [showReinserirDrawer, setShowReinserirDrawer] = useState(false);
  const [showOperacaoDrawer, setShowOperacaoDrawer] = useState(false);
  const [quantidadeReinserir, setQuantidadeReinserir] = useState('');
  const [operacaoDestino, setOperacaoDestino] = useState(null);
  const [operacoesDisponiveis, setOperacoesDisponiveis] = useState([]);

  useEffect(() => {
    if (isSimulation) {
      // Modo simulação - carregar utilizador via API
      loadSimulatedUser();
    } else {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }
    }
    loadResgates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSimulation, routeUsername]);

  const loadSimulatedUser = async () => {
    try {
      const response = await axios.get(`${API}/operadores`);
      const operador = response.data.find(op => op.username === routeUsername);
      if (operador) {
        setUser({
          id: operador.id,
          nome: operador.nome || operador.username,
          username: operador.username
        });
      }
    } catch (error) {
      console.error('Erro ao carregar utilizador simulado:', error);
    }
  };

  const handleBack = () => {
    if (isSimulation) {
      window.location.href = `/sim/operador/${routeUsername}`;
    } else {
      navigate('/operador');
    }
  };

  const loadResgates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/resgates?status=em_arranjo`);
      // Filtrar também os parcialmente reinseridos
      const resgatesAtivos = response.data.filter(r => 
        r.status === 'em_arranjo' || r.status === 'parcialmente_reinserido'
      );
      setResgates(resgatesAtivos);
    } catch (error) {
      console.error('Erro ao carregar resgates:', error);
      toast.error('Erro ao carregar resgates');
    } finally {
      setLoading(false);
    }
  };

  const loadOperacoesDisponiveis = async (resgate) => {
    try {
      // Carregar detalhes do resgate com operações
      const response = await axios.get(`${API}/resgates/${resgate.id}`);
      const resgateDetalhado = response.data;
      
      // Carregar operações da BD para obter nomes
      const operacoesRes = await axios.get(`${API}/operacoes`);
      const operacoesMap = {};
      operacoesRes.data.forEach(op => {
        operacoesMap[op.id] = op;
      });
      
      // Mapear operações em falta com nomes
      const ops = resgateDetalhado.operacoes_falta?.map(opId => ({
        id: opId,
        nome: operacoesMap[opId]?.nome || 'Operação',
        feita: resgateDetalhado.operacoes_feitas?.includes(opId) || false
      })) || [];
      
      setOperacoesDisponiveis(ops);
    } catch (error) {
      console.error('Erro ao carregar operações:', error);
    }
  };

  const handleSelectResgate = async (resgate) => {
    setSelectedResgate(resgate);
    await loadOperacoesDisponiveis(resgate);
    setShowReinserirDrawer(true);
  };

  const handleReinserir = async () => {
    if (!quantidadeReinserir || parseInt(quantidadeReinserir) <= 0) {
      toast.error('Indique a quantidade a reinserir');
      return;
    }
    
    if (!operacaoDestino) {
      toast.error('Selecione a operação de destino');
      return;
    }
    
    if (parseInt(quantidadeReinserir) > selectedResgate.quantidade_total) {
      toast.error('Quantidade maior que disponível');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/resgates/${selectedResgate.id}/reinserir?operador_id=${user?.id || 'operador'}`, {
        resgate_id: selectedResgate.id,
        quantidade: parseInt(quantidadeReinserir),
        operacao_destino_id: operacaoDestino.id,
        observacoes: `Peças reinseridas pelo operador ${user?.nome_completo || user?.username || ''}`
      });
      
      toast.success(`${quantidadeReinserir} peças reinseridas na produção!`);
      setShowReinserirDrawer(false);
      setShowOperacaoDrawer(false);
      setQuantidadeReinserir('');
      setOperacaoDestino(null);
      setSelectedResgate(null);
      loadResgates();
    } catch (error) {
      console.error('Erro ao reinserir peças:', error);
      toast.error(getErrorMessage(error, 'Erro ao reinserir peças'));
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarOperacaoFeita = async (operacao) => {
    if (!selectedResgate) return;
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/resgates/${selectedResgate.id}/marcar-operacao-feita?operacao_id=${operacao.id}&operador_id=${user?.id || 'operador'}`
      );
      
      toast.success(`Operação "${operacao.nome}" marcada como concluída!`);
      await loadOperacoesDisponiveis(selectedResgate);
      loadResgates();
    } catch (error) {
      console.error('Erro ao marcar operação:', error);
      toast.error(getErrorMessage(error, 'Erro ao marcar operação'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Banner de simulação */}
      {isSimulation && (
        <div className="bg-indigo-600 text-white text-center py-1 text-xs font-medium">
          Modo Simulação: {user?.nome || routeUsername}
        </div>
      )}
      
      {/* Header */}
      <header className="bg-white border-b border-stone-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button 
            onClick={handleBack}
            className="p-2 hover:bg-stone-100 rounded-full transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-stone-600" />
          </button>
          <div>
            <h1 className="font-heading text-lg text-navy">Peças em Arranjo</h1>
            <p className="text-xs text-stone-500">Reinserir peças na produção</p>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4">
        {/* Resumo */}
        <Card className="p-4 bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-200 rounded-full">
              <Package className="h-6 w-6 text-amber-700" />
            </div>
            <div>
              <p className="text-sm text-amber-700">Resgates Activos</p>
              <p className="text-2xl font-bold text-amber-800">{resgates.length}</p>
            </div>
          </div>
        </Card>

        {/* Lista de Resgates */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 text-stone-400 animate-spin" />
          </div>
        ) : resgates.length === 0 ? (
          <Card className="p-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-3" />
            <p className="text-stone-600">Não há peças em arranjo</p>
            <p className="text-sm text-stone-400">Todas as peças foram reinseridas</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {resgates.map((resgate) => (
              <Card 
                key={resgate.id} 
                className="p-4 hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-amber-400"
                onClick={() => handleSelectResgate(resgate)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="font-semibold text-navy">{resgate.numero_resgate}</span>
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                      resgate.status === 'em_arranjo' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {resgate.status === 'em_arranjo' ? 'Em Arranjo' : 'Parcial'}
                    </span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-stone-400" />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-amber-600">
                      <Package className="h-4 w-4" />
                      <span className="font-bold">{resgate.quantidade_total}</span>
                      <span className="text-stone-500">peças</span>
                    </div>
                    <div className="flex items-center gap-1 text-stone-500">
                      <Clock className="h-4 w-4" />
                      <span>{resgate.operacoes_falta?.length || 0} op. em falta</span>
                    </div>
                  </div>
                </div>
                
                {/* Detalhes das anomalias */}
                {resgate.anomalias && resgate.anomalias.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-stone-100">
                    <p className="text-xs text-stone-500 mb-1">Origem:</p>
                    {resgate.anomalias.slice(0, 2).map((a, idx) => (
                      <div key={idx} className="text-xs text-stone-600">
                        • {a.operacao_nome || 'Operação'} - {a.quantidade} peças (etapa {a.operacao_ordem || '?'}/{a.total_operacoes_of || '?'})
                      </div>
                    ))}
                    {resgate.anomalias.length > 2 && (
                      <div className="text-xs text-stone-400">+ {resgate.anomalias.length - 2} mais...</div>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Botão Actualizar */}
        <Button 
          variant="outline" 
          className="w-full" 
          onClick={loadResgates}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar Lista
        </Button>
      </main>

      {/* Drawer Detalhes do Resgate */}
      <Drawer open={showReinserirDrawer} onOpenChange={setShowReinserirDrawer}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="border-b border-stone-100 pb-4">
            <DrawerTitle className="text-xl font-heading text-navy">
              {selectedResgate?.numero_resgate}
            </DrawerTitle>
            <DrawerDescription className="text-stone-500">
              {selectedResgate?.quantidade_total} peças em arranjo
            </DrawerDescription>
          </DrawerHeader>
          
          <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
            {/* Info do Resgate */}
            <div className="bg-amber-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-stone-600">Peças disponíveis:</span>
                <span className="text-2xl font-bold text-amber-700">{selectedResgate?.quantidade_total}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-500">Operações em falta:</span>
                <span className="font-medium">{selectedResgate?.operacoes_falta?.length || 0}</span>
              </div>
            </div>

            {/* Lista de Operações */}
            <div>
              <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Operações em Falta
              </h4>
              <div className="space-y-2">
                {operacoesDisponiveis.map((op, idx) => (
                  <div 
                    key={op.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      op.feita ? 'bg-green-50 border-green-200' : 'bg-white border-stone-200'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        op.feita ? 'bg-green-500 text-white' : 'bg-stone-200 text-stone-600'
                      }`}>
                        {op.feita ? '✓' : idx + 1}
                      </span>
                      <span className={op.feita ? 'text-green-700' : 'text-stone-700'}>{op.nome}</span>
                    </div>
                    {!op.feita && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-green-300 text-green-700 hover:bg-green-50"
                        onClick={() => handleMarcarOperacaoFeita(op)}
                        disabled={loading}
                      >
                        Concluída
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Reinserir Peças */}
            <div className="border-t border-stone-200 pt-4">
              <h4 className="font-medium text-stone-700 mb-3">Reinserir Peças na Produção</h4>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-stone-600 mb-1">Quantidade a reinserir</label>
                  <Input
                    type="number"
                    min="1"
                    max={selectedResgate?.quantidade_total || 0}
                    placeholder="Ex: 5"
                    value={quantidadeReinserir}
                    onChange={(e) => setQuantidadeReinserir(e.target.value)}
                    className="h-12 rounded-xl text-center text-xl font-bold"
                  />
                </div>

                <div>
                  <label className="block text-sm text-stone-600 mb-1">Operação de destino</label>
                  <Button
                    variant="outline"
                    className="w-full h-12 justify-between"
                    onClick={() => setShowOperacaoDrawer(true)}
                  >
                    {operacaoDestino ? operacaoDestino.nome : 'Selecionar operação...'}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 h-12 rounded-full"
                onClick={() => {
                  setShowReinserirDrawer(false);
                  setQuantidadeReinserir('');
                  setOperacaoDestino(null);
                }}
              >
                Fechar
              </Button>
              <Button
                className="flex-1 h-12 rounded-full bg-green-500 hover:bg-green-600 text-white font-semibold"
                onClick={handleReinserir}
                disabled={loading || !quantidadeReinserir || !operacaoDestino}
              >
                {loading ? 'A processar...' : 'Reinserir Peças'}
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Drawer Selecionar Operação */}
      <Drawer open={showOperacaoDrawer} onOpenChange={setShowOperacaoDrawer}>
        <DrawerContent>
          <DrawerHeader className="border-b border-stone-100 pb-4">
            <DrawerTitle className="text-lg font-heading text-navy">Selecionar Operação</DrawerTitle>
            <DrawerDescription className="text-stone-500">
              Em que operação quer reinserir as peças?
            </DrawerDescription>
          </DrawerHeader>
          <div className="p-4 space-y-2 max-h-[50vh] overflow-y-auto">
            {operacoesDisponiveis.filter(op => !op.feita).map((op, idx) => (
              <button
                key={op.id}
                onClick={() => {
                  setOperacaoDestino(op);
                  setShowOperacaoDrawer(false);
                }}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  operacaoDestino?.id === op.id 
                    ? 'border-green-400 bg-green-50' 
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-sm font-bold text-stone-600">
                    {idx + 1}
                  </span>
                  <span className="font-medium">{op.nome}</span>
                </div>
              </button>
            ))}
            {operacoesDisponiveis.filter(op => !op.feita).length === 0 && (
              <p className="text-center text-stone-500 py-4">
                Todas as operações já foram concluídas
              </p>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
